<template>
  <div class="the-notifications">
    <button
      class="icon notif-bell"
      title="Notificações"
      @click.stop="toggle"
    >
      <i class="fas fa-bell" />
      <span
        v-if="unreadCount > 0"
        class="notif-badge"
      >{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
    </button>

    <aside
      v-show="open"
      class="notif-panel"
    >
      <div class="notif-panel__head">
        <span class="notif-panel__title">Notificações</span>
        <div
          v-if="items.length"
          class="notif-panel__actions"
        >
          <button
            v-if="unreadCount > 0"
            title="Marcar todas como lidas"
            @click="markAll"
          >
            <i
              class="fas fa-envelope-open"
              title="Marcar todas como lidas"
            />
          </button>
        </div>
      </div>

      <ul class="notif-list">
        <li
          v-if="!items.length"
          class="notif-empty"
        >
          Nenhuma notificação.
        </li>
        <li
          v-for="n in items"
          :key="n.id"
          :class="{ 'is-unread': !n.read_at }"
          class="notif-item"
          @click="onClick(n)"
        >
          <span
            class="notif-dot"
            :class="{ 'is-hidden': n.read_at }"
          />
          <div class="notif-item__content">
            <strong class="notif-item__title">{{ n.title }}</strong>
            <span
              v-if="n.body"
              class="notif-item__body"
            >{{ n.body }}</span>
            <span class="notif-item__time">{{ timeAgo(n.created_at) }}</span>
          </div>
          <div
            class="notif-item__actions"
            :class="{ 'is-confirming': confirmingId === n.id }"
          >
            <template v-if="confirmingId === n.id">
              <span class="notif-item__confirm">
                Excluir?
                <button
                  class="notif-item__confirm-yes"
                  @click.stop="confirmDelete(n.id)"
                >Sim</button>
                <button
                  class="notif-item__confirm-no"
                  @click.stop="confirmingId = null"
                >Não</button>
              </span>
            </template>
            <template v-else>
              <button
                class="notif-item__action"
                :title="n.read_at ? 'Marcar como não lida' : 'Marcar como lida'"
                @click.stop="toggleRead(n)"
              >
                <i :class="n.read_at ? 'fas fa-envelope' : 'fas fa-envelope-open'" />
              </button>
              <button
                class="notif-item__action notif-item__delete"
                title="Apagar"
                @click.stop="confirmingId = n.id"
              >
                <i class="fas fa-times" />
              </button>
            </template>
          </div>
        </li>
      </ul>

      <div
        v-if="pushSupported"
        class="notif-panel__push"
      >
        <button
          v-if="!pushEnabled"
          class="notif-panel__push-btn"
          :disabled="pushBusy"
          @click="enablePush"
        >
          <i class="fas fa-bell" />
          {{ pushBusy ? 'Ativando…' : 'Ativar notificações neste dispositivo' }}
        </button>
        <span
          v-else
          class="notif-panel__push-on"
        >
          <i class="fas fa-check-circle" /> Notificações ativadas neste dispositivo
        </span>
      </div>
    </aside>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { pushVapidKey, pushSubscribe } from '@/services/v1/notifications';

// Verificação incremental a cada 5 min (busca só o que chegou depois da última).
const POLL_MS = 5 * 60 * 1000;

export default {
  name: 'TheNotifications',
  data() {
    return {
      open: false,
      pollId: null,
      confirmingId: null,
      pushEnabled: false,
      pushBusy: false,
    };
  },
  computed: {
    ...mapGetters('notifications', ['items', 'unreadCount']),
    pushSupported() {
      return typeof navigator !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window;
    },
  },
  mounted() {
    // Abertura: recebe tudo (snapshot) + contador.
    this.poll();
    // Depois, incremental a cada 5 min.
    this.pollId = setInterval(() => this.poll(), POLL_MS);
    document.addEventListener('click', this.onOutside);
    this.initPush();
  },
  beforeDestroy() {
    if (this.pollId) clearInterval(this.pollId);
    document.removeEventListener('click', this.onOutside);
  },
  methods: {
    ...mapActions('notifications', [
      'fetch',
      'poll',
      'markRead',
      'markUnread',
      'markAll',
      'markAllUnread',
      'deleteOne',
      'deleteAll',
    ]),
    toggleRead(n) {
      if (n.read_at) this.markUnread(n.id);
      else this.markRead(n.id);
    },
    toggle() {
      this.open = !this.open;
      this.confirmingId = null;
      if (this.open) this.fetch();
    },
    confirmDelete(id) {
      this.deleteOne(id);
      this.confirmingId = null;
    },
    async initPush() {
      await this.checkPushState();
      // Ao logar: se o push é suportado e a permissão ainda NÃO foi decidida
      // (nem concedida nem negada), pede uma vez por sessão. Se já foi negada
      // ('denied') ou concedida ('granted'), não insiste. O botão do sininho
      // segue como fallback (inclusive p/ navegadores que exigem gesto).
      if (
        this.pushSupported
        && !this.pushEnabled
        && typeof Notification !== 'undefined'
        && Notification.permission === 'default'
        && !sessionStorage.getItem('pushPromptedThisSession')
      ) {
        sessionStorage.setItem('pushPromptedThisSession', '1');
        this.enablePush();
      }
    },
    async checkPushState() {
      if (!this.pushSupported) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        this.pushEnabled = !!sub && Notification.permission === 'granted';
      } catch (e) {
        this.pushEnabled = false;
      }
    },
    urlBase64ToUint8Array(base64String) {
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
      const raw = window.atob(base64);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
      return arr;
    },
    async enablePush() {
      if (!this.pushSupported || this.pushBusy) return;
      this.pushBusy = true;
      try {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          this.pushBusy = false;
          return;
        }
        const reg = await navigator.serviceWorker.register('/push-sw.js');
        await navigator.serviceWorker.ready;
        const { data } = await pushVapidKey();
        const key = this.urlBase64ToUint8Array(data.data.public_key);
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: key,
        });
        await pushSubscribe(sub.toJSON(), navigator.platform || 'Navegador');
        this.pushEnabled = true;
      } catch (e) {
        this.pushEnabled = false;
        // eslint-disable-next-line no-console
        console.error('[push] falha ao ativar', e);
      } finally {
        this.pushBusy = false;
      }
    },
    onClick(n) {
      if (!n.read_at) this.markRead(n.id);
      // Deep-link: se a notificação trouxer uma url, abre em nova aba.
      const url = n.data && (n.data.url || n.data.link);
      if (url) window.open(url, '_blank', 'noopener');
    },
    onOutside(e) {
      if (this.open && this.$el && !this.$el.contains(e.target)) {
        this.open = false;
      }
    },
    timeAgo(dateStr) {
      if (!dateStr) return '';
      const then = new Date(dateStr).getTime();
      if (Number.isNaN(then)) return '';
      const diff = Math.floor((Date.now() - then) / 1000);
      if (diff < 45) return 'agora';
      if (diff < 90) return 'há 1 min';
      const min = Math.floor(diff / 60);
      if (min < 60) return `há ${min} min`;
      const h = Math.floor(min / 60);
      if (h < 24) return `há ${h} h`;
      const d = Math.floor(h / 24);
      if (d < 7) return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
      return new Date(dateStr).toLocaleDateString('pt-BR');
    },
  },
};
</script>

<style lang="scss" scoped>
.the-notifications {
  position: relative;
  display: inline-flex;
}

.notif-bell {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  font-size: 1.1rem;
}

.notif-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #e53e3e;
  color: #fff;
  font-size: 0.625rem;
  line-height: 16px;
  text-align: center;
  font-weight: 700;
}

.notif-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 340px;
  max-width: calc(100vw - 16px);
  max-height: 440px;
  overflow-y: auto;
  overflow-x: hidden;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  z-index: 60;
  padding: 12px 0;

  // Mobile (maior uso): o painel vira "folha" fixa ocupando a largura,
  // com margem e rolagem própria — não corta nem força scroll horizontal.
  @media (max-width: 600px) {
    position: fixed;
    top: 60px;
    left: 8px;
    right: 8px;
    width: auto;
    max-width: none;
    max-height: calc(100vh - 76px);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 16px 10px;
    border-bottom: 1px solid #edf0f3;
  }

  &__title {
    font-weight: 700;
    color: #1a202c;
  }

  &__actions {
    display: flex;
    gap: 4px;

    button {
      background: none;
      border: none;
      color: #a0aec0;
      font-size: 0.95rem;
      cursor: pointer;
      padding: 2px 4px;

      &:hover {
        color: #2f855a;
      }
    }
  }
}

.notif-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.notif-empty {
  padding: 18px 16px;
  color: #718096;
  font-size: 0.85rem;
  text-align: center;
}

.notif-item {
  position: relative;
  display: flex;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f4f6f8;

  &:hover {
    background: #f7fafc;
  }

  &:hover .notif-item__actions {
    visibility: visible;
  }

  &.is-unread {
    background: #f0fff4;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  &__title {
    color: #1a202c;
    font-size: 0.875rem;
  }

  &__body {
    color: #4a5568;
    font-size: 0.8rem;
  }

  &__time {
    color: #a0aec0;
    font-size: 0.7rem;
    margin-top: 2px;
  }

  &__actions {
    visibility: hidden;
    flex: 0 0 auto;
    display: flex;
    gap: 2px;
    align-self: flex-start;
  }

  &__action {
    background: none;
    border: none;
    color: #a0aec0;
    cursor: pointer;
    padding: 2px 4px;

    &:hover {
      color: #2f855a;
    }
  }

  &__delete:hover {
    color: #c53030;
  }

  &__actions.is-confirming {
    visibility: visible;
  }

  &__confirm {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.72rem;
    color: #9b2c2c;
    white-space: nowrap;
    background: #fff5f5;
    border: 1px solid #fed7d7;
    border-radius: 999px;
    padding: 3px 10px;

    button {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 0.72rem;
      padding: 0;
      line-height: 1;
    }
  }

  &__confirm-yes {
    color: #c53030;
    font-weight: 700;
  }

  &__confirm-no {
    color: #718096;
    font-weight: 600;
  }
}

.notif-panel__push {
  padding: 10px 16px 2px;
  border-top: 1px solid #edf0f3;
  margin-top: 4px;
}

.notif-panel__push-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
  background: #f0fff4;
  border: 1px solid #c6f6d5;
  color: #2f855a;
  border-radius: 8px;
  padding: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.notif-panel__push-on {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #2f855a;
  font-size: 0.78rem;
}

.notif-dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: #2f855a;

  &.is-hidden {
    visibility: hidden;
  }
}
</style>
